import { useState, type FormEvent } from 'react';
import { ArrowUp } from 'lucide-react';
import type { ConnectorStatus } from '../../../lib/connectors';
import { comingSoonContext, CONNECTOR_REQUEST_CONTEXT, type ComingSoonConnector } from '../../../lib/connector-requests';
import { CONNECTORS_PAGE } from '../../../lib/use-connectors';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { Card, IconButton, Input } from '../ui';
import styles from './connectors.module.css';
import mobileStyles from './mobile/mobile.module.css';

/** A coming-soon app drawn by the ordinary connector card: it is meant to read as one of the apps to connect. */
export function comingSoonRow(connector: ComingSoonConnector): ConnectorStatus {
  return { toolkit: connector.toolkit, name: connector.name, logo: connector.logo, description: connector.description, connected: false };
}

/** Opens a Maestro chat about a connector Symphony cannot connect; see `lib/connector-requests.ts`. */
export function useAskForConnector() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  return {
    askComingSoon: (connector: ConnectorStatus) => {
      const name = connector.name || connector.toolkit;
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `coming_soon_${connector.toolkit}` });
      openSideChat({ kind: 'new', message: t('connectors.comingSoon.seed', { name }), context: comingSoonContext(name) });
    },
    askForRequest: (request: string) => {
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'request_connector' });
      openSideChat({ kind: 'new', message: t('connectors.request.seed', { request }), context: CONNECTOR_REQUEST_CONTEXT });
    },
  };
}

/**
 * The last card of "Available to connect": no logo and no name, a question and a field. What the user types
 * opens a Maestro chat that connects the app if it turns out to exist, and passes it to the team if not.
 * `initialText` carries a search that found nothing, so the reader does not type it twice.
 */
export function RequestConnectorCard({ initialText = '', compact = false }: { initialText?: string; compact?: boolean }) {
  const { t } = useTranslation();
  const { askForRequest } = useAskForConnector();
  const [text, setText] = useState(initialText);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const request = text.trim();
    if (!request) return;
    askForRequest(request);
    setText('');
  };
  const form = (
    <form className={styles.requestForm} onSubmit={submit}>
      <Input
        size="large"
        className={styles.requestInput}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={t('connectors.request.placeholder')}
        aria-label={t('connectors.request.title')}
        maxLength={200}
      />
      <IconButton type="submit" size="large" variant="contained" disabled={!text.trim()} aria-label={t('connectors.request.send')} title={t('connectors.request.send')}>
        <ArrowUp size={16} aria-hidden />
      </IconButton>
    </form>
  );
  if (compact) {
    return (
      <Card className={`${mobileStyles.connectorCard} ${mobileStyles.cardShadow}`} data-testid="request-connector-card">
        <div className={styles.requestMain}>
          <div className={styles.requestPlate} style={{ width: 28, height: 28, borderRadius: 8 }} aria-hidden />
          <div className={mobileStyles.rowName}>{t('connectors.request.title')}</div>
        </div>
        {form}
      </Card>
    );
  }
  return (
    <div className={`${styles.card} ${styles.requestCard}`} data-testid="request-connector-card">
      <div className={styles.requestMain}>
        <div className={styles.requestPlate} aria-hidden />
        <div className={`sym-h4 ${styles.name}`}>{t('connectors.request.title')}</div>
      </div>
      {form}
    </div>
  );
}
